import { Fragment, useEffect, useState, useCallback } from "react";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { Outlet, useNavigate } from "react-router-dom";
import {
  Bars3BottomLeftIcon,
  BellIcon,
  XMarkIcon,
  UserIcon,
  BuildingLibraryIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { Logo } from "../utils/logo";
import { useSelector } from "react-redux";
import SidebarNav from "./sidebar-nav";
import { TWclassNames } from "../utils/Div";
import { getMyCompanies, switchCompany } from "../../api/user";
import { tasksApi } from "../../api/tasks";
import { toast } from "react-hot-toast";

const userNavigation = [
  { title: "Your Profile", href: "/dashboard/user/profile" },
  { title: "Settings", href: "/dashboard/user/settings" },
  {
    title: "Microsoft / Outlook",
    href: "/dashboard/user/microsoft",
  },
  { title: "Sign out", href: "/authentication/logout", active: true },
];

function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
}

export const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const navigate = useNavigate();

  const windowSize = useWindowSize();
  const currentUser = useSelector((state) => state.session.currentUser);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await getMyCompanies();
      if (response.companies) {
        setCompanies(response.companies);
      }
    } catch (error) {
      console.error("Failed to load companies:", error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadTasks = useCallback(async () => {
    setLoadingTasks(true);
    try {
      const response = await tasksApi.getTasks({ finished: false, active: true });
      if (response?.data?.success) {
        setTasks(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to load tasks:", error);
    } finally {
      setLoadingTasks(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadCompanies();
      loadTasks();
    }
  }, [currentUser, loadTasks]);

  // Refresh tasks periodically (every 5 minutes)
  useEffect(() => {
    if (currentUser) {
      const interval = setInterval(() => {
        loadTasks();
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(interval);
    }
  }, [currentUser, loadTasks]);

  const currentCompany = companies.find((c) => c.current) || null;

  const handleCompanySwitch = async (companyId) => {
    try {
      const response = await switchCompany(companyId);
      if (response.companies) {
        setCompanies(response.companies);
        toast.success("Bedrift byttet");
        // Reload page to update all company-dependent data
        window.location.reload();
      }
    } catch (error) {
      toast.error(error?.error || "Kunne ikke bytte bedrift");
    }
  };

  // Calculate task statistics
  const unfinishedTasks = tasks.filter((t) => !t.finished);
  const unfinishedCount = unfinishedTasks.length;

  // Helper function to check if date is today
  const isToday = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Helper function to check if date has passed
  const isOverdue = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Filter tasks by due date (if due_date field exists)
  const tasksDueToday = unfinishedTasks.filter((t) => t.due_date && isToday(t.due_date));
  const overdueTasks = unfinishedTasks.filter((t) => t.due_date && isOverdue(t.due_date));

  const handleClick = () => {
    if (windowSize.width != null && windowSize.width <= 768) {
      setSidebarOpen(false);
    }
  };

  const desktopContentOffset =
    typeof windowSize.width === "number" && windowSize.width >= 768
      ? collapsed
        ? "4rem"
        : "16rem"
      : undefined;

  return (
    <div className="min-h-full">
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 md:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white pt-16 pb-4">
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
                <div className="mt-5 h-0 flex-1 overflow-y-auto">
                  <nav className="space-y-0 px-2">
                    <SidebarNav
                      onClick={() => handleClick()}
                      collapsed={false}
                    />
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 flex-shrink-0" aria-hidden="true" />
          </div>
        </Dialog>
      </Transition.Root>

      <div
        className="hidden md:fixed md:left-0 md:top-16 md:bottom-0 md:flex md:flex-col transition-all duration-300 z-50"
        onMouseEnter={() => setCollapsed(false)}
        onMouseLeave={() => setCollapsed(true)}
        style={{ width: collapsed ? "4rem" : "16rem" }}
      >
        <div className="flex flex-grow flex-col overflow-y-auto bg-white pt-5 rounded-xl shadow-xl">
          <div className="mt-5 flex flex-1 flex-col">
            <nav className="flex-1 space-y-0 px-2 pb-4">
              <SidebarNav onClick={handleClick} collapsed={collapsed} />
            </nav>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        <div className="sticky top-0 z-50 flex h-16 flex-shrink-0 bg-white shadow px-4 items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              type="button"
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Bars3BottomLeftIcon className="h-6 w-6" />
            </button>
            <Logo emblemOnly color="dark" sizeClass="h-10" />
          </div>

          <div className="flex items-center space-x-4">
            <button
              type="button"
              className="rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <span className="sr-only">View notifications</span>
              <BellIcon className="h-6 w-6" />
            </button>

            <Menu as="div" className="relative">
              <div>
                <Menu.Button className="relative rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                  <span className="sr-only">View tasks</span>
                  <ClipboardDocumentListIcon className="h-6 w-6" />
                  {unfinishedCount > 0 && (
                    <span className="absolute top-0 right-0 block h-4 w-4 rounded-full bg-red-500 ring-2 ring-white">
                      <span className="absolute top-0 left-0 flex h-full w-full items-center justify-center text-[10px] font-bold text-white">
                        {unfinishedCount > 9 ? "9+" : unfinishedCount}
                      </span>
                    </span>
                  )}
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-80 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">Oppgaver</h3>
                  </div>
                  {loadingTasks ? (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      Laster oppgaver...
                    </div>
                  ) : (
                    <>
                      <div className="px-4 py-2 space-y-1">
                        <div className="flex items-center justify-between py-2">
                          <span className="text-sm text-gray-700">Ikke ferdige</span>
                          <span className="text-sm font-semibold text-gray-900">{unfinishedCount}</span>
                        </div>
                        {tasksDueToday.length > 0 && (
                          <div className="flex items-center justify-between py-2 border-t border-gray-100">
                            <span className="text-sm text-gray-700">Forfaller idag</span>
                            <span className="text-sm font-semibold text-orange-600">{tasksDueToday.length}</span>
                          </div>
                        )}
                        {overdueTasks.length > 0 && (
                          <div className="flex items-center justify-between py-2 border-t border-gray-100">
                            <span className="text-sm text-gray-700">Har forfalt</span>
                            <span className="text-sm font-semibold text-red-600">{overdueTasks.length}</span>
                          </div>
                        )}
                      </div>
                      <div className="border-t border-gray-200">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => navigate("/dashboard/todos")}
                              className={TWclassNames(
                                active ? "bg-gray-100" : "",
                                "w-full text-left flex items-center justify-between px-4 py-2 text-sm text-gray-700"
                              )}
                            >
                              <span>Gå til oppgaver</span>
                              <ArrowRightIcon className="h-4 w-4" />
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </>
                  )}
                </Menu.Items>
              </Transition>
            </Menu>

            <Menu as="div" className="relative">
              <div>
                <Menu.Button className="flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 border border-gray-300">
                  <BuildingLibraryIcon className="h-5 w-5 text-gray-500" />
                  {currentCompany ? (
                    <span className="max-w-[200px] truncate">{currentCompany.name}</span>
                  ) : (
                    <span>Bedrifter</span>
                  )}
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-96 overflow-y-auto">
                  {loadingCompanies ? (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      Laster bedrifter...
                    </div>
                  ) : companies.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      Ingen bedrifter tilgjengelig
                    </div>
                  ) : (
                    companies.map((company) => (
                      <Menu.Item key={company.id}>
                        {({ active }) => (
                          <button
                            onClick={() => handleCompanySwitch(company.id)}
                            className={TWclassNames(
                              active ? "bg-gray-100" : "",
                              company.current ? "bg-blue-50" : "",
                              "w-full text-left flex items-center justify-between px-4 py-2 text-sm text-gray-700"
                            )}
                          >
                            <span className="flex-1">{company.name}</span>
                            {company.current && (
                              <CheckIcon className="h-5 w-5 text-blue-600" />
                            )}
                          </button>
                        )}
                      </Menu.Item>
                    ))
                  )}
                </Menu.Items>
              </Transition>
            </Menu>

            <Menu as="div" className="relative">
              <div>
                <Menu.Button className="flex items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                  <span className="sr-only">Open user menu</span>
                  <UserIcon className="h-8 w-8 rounded-full" />
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {userNavigation.map((item) => (
                    <Menu.Item key={item.title}>
                      {({ active }) => (
                        <a
                          href={item.href}
                          className={TWclassNames(
                            active ? "bg-gray-100" : "",
                            "block px-4 py-2 text-sm text-gray-700"
                          )}
                        >
                          {item.title}
                        </a>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>

        <main
          className="mb-0 sm:mb-20 md:transition-[padding-left] md:duration-300 md:ease-in-out"
          style={{ paddingLeft: desktopContentOffset }}
        >
          <Outlet />
        </main>
      </div>
      {/* <LocaleDebugger /> */}
    </div>
  );
};
export default DashboardLayout;
